/**
 * File: src/helper-functions/livestock/config/species-ageclasses.js
 * Purpose: Age class ranges and helpers per species.
 * Version: 0.12
 * Date: 2025-10-10
 */
export const speciesAgeClassRanges = {
    alpaca: {
        cria: { min: 0, max: 180, adg: 0.18, mjDay: 18 },
        weanling: { min: 180, max: 365, adg: 0.16, mjDay: 20 },
        yearling: { min: 365, max: 730, adg: 0.14, mjDay: 22 },
        tui: { min: 365, max: 730, adg: 0.14, mjDay: 22 }, // alias of yearling
        hembra: { min: 365, max: Infinity, gender: 'female', adg: null, mjDay: 24 }, // maintenance (preg/lact handled elsewhere)
        macho: { min: 365, max: Infinity, gender: 'male', adg: 0.08, mjDay: 26 }, // slow mature gain or set adg:null if strictly maintenance
    },
    bison: {
        calf: { min: 0, max: 180, adg: 0.7, mjDay: 25 },
        weanling: { min: 180, max: 365, adg: 0.55, mjDay: 28 },
        yearling: { min: 365, max: 730, adg: 0.45, mjDay: 30 },
        heifer: { min: 365, max: 1095, gender: 'female', calved: false, adg: 0.25, mjDay: 32 },
        cow: { min: 730, max: Infinity, gender: 'female', calved: true, adg: null, mjDay: 34 },
        bull: { min: 730, max: Infinity, gender: 'male', intact: true, adg: 0.3, mjDay: 40 },
        steer: { min: 365, max: Infinity, gender: 'male', castrated: true, adg: 0.35, mjDay: 35 },
    },
    bee: {
        egg: { min: 0, max: 3 },
        larva: { min: 3, max: 9 },
        pupa: { min: 9, max: 21 },
        adult: { min: 21, max: Infinity },
        queen: { min: 21, max: Infinity, caste: 'queen' },
        worker: { min: 21, max: Infinity, caste: 'worker', gender: 'female' },
        drone: { min: 21, max: Infinity, caste: 'drone', gender: 'male' },
    },
    camel: {
        calf: { min: 0, max: 365, adg: 0.35, mjDay: 45 },
        yearling: { min: 365, max: 730, adg: 0.3, mjDay: 55 },
        cow: { min: 730, max: Infinity, gender: 'female', adg: 0.15, mjDay: 70 },
        bull: { min: 730, max: Infinity, gender: 'male', intact: true, adg: 0.2, mjDay: 80 },
    },
    cattle: {
        'neonate calf': { min: 0, max: 7, adgMin: 0.4, adgDefault: 0.6, adgMax: 0.8, mjDay: 30 },

        calf: { min: 7, max: 180, adgMin: 0.7, adgDefault: 0.8, adgMax: 0.9, mjDay: 55 },
        vealer: { min: 180, max: 270, adgMin: 0.7, adgDefault: 0.8, adgMax: 0.9, mjDay: 75 },
        weanling: { min: 180, max: 365, adgMin: 0.6, adgDefault: 0.7, adgMax: 0.8, mjDay: 70 },
        yearling: { min: 365, max: 730, adgMin: 0.6, adgDefault: 0.75, adgMax: 0.9, mjDay: 90 },

        // females
        heifer: { min: 365, max: 1095, gender: 'female', calved: false, adgMin: 0.5, adgDefault: 0.6, adgMax: 0.7, mjDay: 95 },
        springer: { min: 600, max: 730, gender: 'female', pregnant: true, adgMin: 0.0, adgDefault: 0.2, adgMax: 0.3, mjDay: 105 },

        // breeders: ADG isn't a great KPI; keep null to avoid fake “growth”
        cow: { min: 730, max: Infinity, gender: 'female', calved: true, adgDefault: null, mjDay: 120 },

        // optional: if you *do* want a number for “condition rebuild” in dry period
        'dry cow': { min: 730, max: Infinity, gender: 'female', calved: true, lactating: false, adgMin: 0.0, adgDefault: 0.15, adgMax: 0.25, mjDay: 80 },
        'cull cow': { min: 1825, max: Infinity, gender: 'female', cull: true, adgMin: 0.0, adgDefault: 0.15, adgMax: 0.3, mjDay: 85 },

        // males
        'bull calf': { min: 0, max: 180, gender: 'male', intact: true, adgMin: 0.75, adgDefault: 0.85, adgMax: 0.95, mjDay: 60 },

        // mature breeding bulls are generally maintenance; young growing bulls are different
        bull: { min: 730, max: Infinity, gender: 'male', intact: true, adgMin: 0.0, adgDefault: 0.3, adgMax: 0.8, mjDay: 130 },
        'cull bull': { min: 1825, max: Infinity, gender: 'male', intact: true, cull: true, adgMin: 0.0, adgDefault: 0.15, adgMax: 0.3, mjDay: 120 },

        steer: { min: 365, max: 1095, gender: 'male', castrated: true, adgMin: 0.6, adgDefault: 0.8, adgMax: 0.9, mjDay: 105 },
        bullock: { min: 730, max: Infinity, gender: 'male', castrated: true, adgMin: 0.4, adgDefault: 0.55, adgMax: 0.7, mjDay: 110 },
    },
    chicken: {
        chick: { min: 0, max: 42, adg: 0.045, mjDay: 3.8 },
        pullet: { min: 42, max: 365, gender: 'female', adg: 0.02, mjDay: 2.8 },
        cockerel: { min: 42, max: 365, gender: 'male', intact: true, adg: 0.03, mjDay: 3.2 },
        hen: { min: 365, max: Infinity, gender: 'female', adg: null, mjDay: 3.5 },
        rooster: { min: 365, max: Infinity, gender: 'male', intact: true, adg: null, mjDay: 3.2 },
    },
    deer: {
        fawn: { min: 0, max: 180, adg: 0.25, mjDay: 10 },
        weanling: { min: 180, max: 365, adg: 0.22, mjDay: 12 },
        yearling: { min: 365, max: 730, adg: 0.18, mjDay: 15 },
        doe: { min: 365, max: Infinity, gender: 'female', adg: null, mjDay: 18 },
        buck: { min: 365, max: Infinity, gender: 'male', intact: true, adg: null, mjDay: 20 },
    },
    donkey: {
        foal: { min: 0, max: 180, adg: 0.2, mjDay: 22 },
        weanling: { min: 180, max: 365, adg: 0.18, mjDay: 28 },
        yearling: { min: 365, max: 730, adg: 0.15, mjDay: 34 },
        jenny: { min: 730, max: Infinity, gender: 'female', adg: null, mjDay: 40 },
        jack: { min: 730, max: Infinity, gender: 'male', intact: true, adg: null, mjDay: 44 },
        gelding: { min: 365, max: Infinity, gender: 'male', castrated: true, adg: null, mjDay: 42 },
    },
    duck: {
        duckling: { min: 0, max: 42, adg: 0.05, mjDay: 2.5 },
        juvenile: { min: 42, max: 112, adg: 0.06, mjDay: 3.5 },
        hen: { min: 112, max: Infinity, gender: 'female', adg: null, mjDay: 4.0 },
        drake: { min: 112, max: Infinity, gender: 'male', intact: true, adg: null, mjDay: 4.2 },
    },
    emu: {
        chick: { min: 0, max: 60, adg: 0.12, mjDay: 10 },
        juvenile: { min: 60, max: 180, adg: 0.2, mjDay: 16 },
        grower: { min: 180, max: 365, adg: 0.25, mjDay: 22 },
        'sub-adult': { min: 365, max: 730, adg: 0.2, mjDay: 24 },
        adult: { min: 730, max: Infinity, adg: null, mjDay: 28 },
        hen: { min: 730, max: Infinity, gender: 'female', adg: null, mjDay: 28 },
        cock: { min: 730, max: Infinity, gender: 'male', intact: true, adg: null, mjDay: 28 },
    },
    fish: {
        egg: { min: 0, max: 5 },
        larva: { min: 5, max: 20 },
        fry: { min: 20, max: 45 },
        fingerling: { min: 45, max: 120 },
        juvenile: { min: 120, max: 240 },
        'grow-out': { min: 240, max: 540 },
        adult: { min: 365, max: Infinity },
        broodstock: { min: 540, max: Infinity, broodstock: true },
    },
    geese: {
        gosling: { min: 0, max: 56, adg: 0.045, mjDay: 2.4 },
        juvenile: { min: 56, max: 168, adg: 0.055, mjDay: 3.6 },
        goose: { min: 168, max: Infinity, gender: 'female', adg: null, mjDay: 5.0 },
        gander: { min: 168, max: Infinity, gender: 'male', intact: true, adg: null, mjDay: 5.2 },
    },
    goat: {
        kid: { min: 0, max: 180, adg: 0.25, mjDay: 15 },
        weanling: { min: 180, max: 365, adg: 0.22, mjDay: 18 },
        yearling: { min: 365, max: 730, adg: 0.18, mjDay: 22 },
        doe: { min: 365, max: Infinity, gender: 'female', adg: 0.12, mjDay: 28 },
        buck: { min: 365, max: Infinity, gender: 'male', intact: true, adg: 0.15, mjDay: 32 },
        wether: { min: 180, max: Infinity, gender: 'male', castrated: true, adg: 0.13, mjDay: 28 },
    },
    horse: {
        foal: { min: 0, max: 180, adg: 0.9, mjDay: 40 },
        weanling: { min: 180, max: 365, adg: 0.7, mjDay: 55 },
        yearling: { min: 365, max: 730, adg: 0.5, mjDay: 65 },
        filly: { min: 365, max: 1460, gender: 'female', calved: false, adg: 0.35, mjDay: 70 },
        colt: { min: 365, max: 1460, gender: 'male', intact: true, adg: 0.4, mjDay: 75 },
        mare: { min: 1460, max: Infinity, gender: 'female', adg: null, mjDay: 75 },
        stallion: { min: 1460, max: Infinity, gender: 'male', intact: true, adg: null, mjDay: 80 },
        gelding: { min: 365, max: Infinity, gender: 'male', castrated: true, adg: null, mjDay: 70 },
    },
    kangaroo: {
        'pouch joey': { min: 0, max: 240, adg: 0.02, mjDay: 3.5 },
        'at-foot joey': { min: 240, max: 365, adg: 0.045, mjDay: 5.0 },
        'sub-adult': { min: 365, max: 730, adg: 0.035, mjDay: 6.5 },
        doe: { min: 730, max: Infinity, gender: 'female', adg: null, mjDay: 7.0 },
        boomer: { min: 730, max: Infinity, gender: 'male', intact: true, adg: null, mjDay: 9.0 },
    },
    llama: {
        cria: { min: 0, max: 180, adg: 0.17, mjDay: 18 },
        weanling: { min: 180, max: 365, adg: 0.15, mjDay: 20 },
        yearling: { min: 365, max: 730, adg: 0.13, mjDay: 22 },
        tui: { min: 365, max: 730, adg: 0.13, mjDay: 22 },
        hembra: { min: 365, max: Infinity, gender: 'female', adg: null, mjDay: 25 },
        macho: { min: 365, max: Infinity, gender: 'male', adg: 0.08, mjDay: 27 },
    },
    ostrich: {
        chick: { min: 0, max: 60, adg: 0.2, mjDay: 12 },
        juvenile: { min: 60, max: 180, adg: 0.28, mjDay: 16 },
        grower: { min: 180, max: 365, adg: 0.32, mjDay: 22 },
        'sub-adult': { min: 365, max: 730, adg: 0.25, mjDay: 26 },
        adult: { min: 730, max: Infinity, adg: null, mjDay: 30 },
        hen: { min: 730, max: Infinity, gender: 'female', adg: null, mjDay: 30 },
        cock: { min: 730, max: Infinity, gender: 'male', intact: true, adg: null, mjDay: 32 },
        rooster: { min: 730, max: Infinity, gender: 'male', intact: true, adg: null, mjDay: 32 }, // alias
    },
    oyster: {
        larva: { min: 0, max: 14 },
        spat: { min: 14, max: 60 },
        adult: { min: 365, max: Infinity },
    },
    pig: {
        'neonatal piglet': { min: 0, max: 7, adg: 0.18, mjDay: 6 },
        'sucking piglet': { min: 7, max: 21, adg: 0.25, mjDay: 12 },
        weaner: { min: 21, max: 70, adg: 0.45, mjDay: 22 },
        grower: { min: 70, max: 180, adg: 0.8, mjDay: 35 },
        finisher: { min: 180, max: 365, adg: 0.9, mjDay: 45 },
        gilt: { min: 180, max: Infinity, gender: 'female', farrowed: false, adg: 0.4, mjDay: 55 },
        sow: { min: 365, max: Infinity, gender: 'female', farrowed: true, adg: null, mjDay: 70 },
        boar: { min: 365, max: Infinity, gender: 'male', intact: true, adg: 0.25, mjDay: 75 },
        barrow: { min: 365, max: Infinity, gender: 'male', castrated: true, adg: 0.35, mjDay: 65 },
    },
    sheep: {
        'newborn lamb': { min: 0, max: 7, adg: 0.25, mjDay: 8 },
        'neonatal lamb': { min: 7, max: 14, adg: 0.3, mjDay: 10 },
        'sucking lamb': { min: 14, max: 30, adg: 0.35, mjDay: 14 },
        weaner: { min: 30, max: 180, adg: 0.2, mjDay: 18 },
        hogget: { min: 180, max: 365, adg: 0.18, mjDay: 22 },
        yearling: { min: 365, max: 730, adg: 0.16, mjDay: 25 },
        ewe: { min: 365, max: Infinity, gender: 'female', adg: 0.1, mjDay: 30 },
        ram: { min: 365, max: Infinity, gender: 'male', intact: true, adg: 0.15, mjDay: 35 },
        wether: { min: 365, max: Infinity, gender: 'male', castrated: true, adg: 0.12, mjDay: 30 },
    },
    turkey: {
        poult: { min: 0, max: 56, adg: 0.065, mjDay: 3.0 },
        'young hen': { min: 56, max: 365, gender: 'female', adg: 0.08, mjDay: 3.8 },
        'young tom': { min: 56, max: 365, gender: 'male', intact: true, adg: 0.095, mjDay: 4.5 },
        hen: { min: 365, max: Infinity, gender: 'female', adg: null, mjDay: 4.0 },
        tom: { min: 365, max: Infinity, gender: 'male', intact: true, adg: null, mjDay: 5.0 },
    },
}

// const firstClass = s => {
//     const ranges = speciesAgeClassRanges[speciesNosLC(s)] || {}
//     const k = Object.keys(ranges)[0]
//     return k ?? null
// }

// export const getAgeClassFromDOB = (species, dateOfBirth, opts = {}) => {
//     if (!species) return null
//     const fallback = firstClass(species)
//     if (!dateOfBirth) return fallback
//     const dob = new Date(dateOfBirth)
//     if (isNaN(+dob)) return fallback
//     const days = differenceInDays(new Date(), dob)
//     if (!Number.isFinite(days) || days < 0) return fallback
//     return resolveSingle(speciesNosLC(species), days, opts) || fallback
// }

// Mammals

// Water buffalo (dairy & meat)

// Yak (dairy, meat, fibre)

// Rabbit (meat; Angora fibre)

// Reindeer (Nordic meat, velvet)

// Wapiti/Elk (farm cervid distinct from deer in some markets)

// Poultry / Gamebirds

// Quail (eggs & meat)

// Pheasant (meat)

// Guinea fowl (eggs & meat)

// Pigeon/Squab (meat)

// Rhea (ratite like emu/ostrich)

// Aquaculture (beyond “fish” & oyster)

// Shrimp/Prawn

// Crayfish/Yabby

// Mussels, Clams, Scallops

// Abalone

// (Optionally keep tilapia/salmon/catfish as subtypes under fish)

// Invertebrates / Other

// Snail (Heliciculture)

// Silkworm (sericulture)

// Black soldier fly (insect protein; feed)

// Reptiles

// Alligator (US; parallel to your Crocodile entry)

// If you want to stay “enterprise-scale only”, I’d prioritise: Water buffalo, Yak, Rabbit, Quail, Prawn/Shrimp, Mussel/Clam/Scallop, Abalone, Crayfish/Yabby, Reindeer/Wapiti, Rhea, Alligator.
// Everything else is nice-to-have/regional.
